import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
import Portal from '@atlaskit/portal/portal';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import SplitButtonGif from '../../../assets/SplitButton.gif';

import { useCwRSpotlightSeenStorage } from './useCwRSpotlightSeenStorage.tsx';
import { useElementBySelector } from './useElementBySelector.ts';

import { UNSAFE_UpdateOnChange as SpotlightUpdateOnChange } from '@atlaskit/spotlight/update-on-change';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import type { DismissEvent, Placement } from '@atlaskit/spotlight/types';

type UniversalCreateCwRSpotlightOnboardingKey =
	(typeof UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS)[keyof typeof UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS];
const ONBOARDING_STATE_KEYS_TO_FETCH = Object.values(
	UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS,
) as UniversalCreateCwRSpotlightOnboardingKey[];

const SPOTLIGHT_ANALYTICS_ID = 'universalCreateCwRExperimentSpotlight';
const CHOREOGRAPHER_BLOCKED_STATUS = 'blocked';
type SpotlightNotDisplayedReason = 'choreographerBlocked' | 'choreographerError';
export type SpotlightVariant = 'button' | 'menu';

type UniversalCreateSpotlightInteractionType = 'cta' | 'dismissButton' | 'blanket' | 'escape';

const spotlightAnchorStyles = cssMap({
	// Anchor for the dropdown chevron spotlight.
	menuAnchorWrapper: {
		display: 'block',
		position: 'absolute',
		top: token('space.400'),
		left: token('space.negative.100'),
		width: '1px',
		height: '1px',
	},
	// Anchor for the primary "Create" button spotlight.
	buttonAnchorWrapper: {
		display: 'block',
		position: 'absolute',
		bottom: token('space.negative.100'),
		left: '50%',
		transform: 'translateX(-50%)',
		width: '1px',
		height: '1px',
	},
});

const RICH_TEXT_VALUES = {
	b: (chunks: React.ReactNode) => <Text weight="bold">{chunks}</Text>,
};

const isKeyboardDismissEvent = (event: DismissEvent): event is KeyboardEvent => 'key' in event;

const isDismissButtonEvent = (
	event: DismissEvent,
): event is React.MouseEvent<HTMLButtonElement, MouseEvent> =>
	'nativeEvent' in event &&
	typeof HTMLButtonElement !== 'undefined' &&
	event.currentTarget instanceof HTMLButtonElement;

export const getDismissInteractionType = (
	event: DismissEvent,
): Exclude<UniversalCreateSpotlightInteractionType, 'cta'> => {
	if (isKeyboardDismissEvent(event)) {
		return 'escape';
	}

	return isDismissButtonEvent(event) ? 'dismissButton' : 'blanket';
};

export type UniversalCreateSpotlightMessages = {
	title: MessageDescriptor;
	bodyLine1: MessageDescriptor;
	bodyLine2: MessageDescriptor;
	cta: MessageDescriptor;
};

export type UseRenderCwRSpotlightArgs = {
	anchorSelector: string;
	choreographerMessageId: string;
	spotlightTarget: SpotlightVariant;
	messages: UniversalCreateSpotlightMessages;
	placement: Placement;
	isEnabled: boolean;
	requireAnchorForStart?: boolean;
	onCtaClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	onClosed?: () => void;
	onBlocked?: (blockedVariant: SpotlightVariant) => void;
};

export type UseRenderCwRSpotlightResult = {
	spotlight: React.ReactNode;
	startSpotlight: () => boolean;
	stopSpotlight: () => void;
	isSpotlightActive: boolean;
};

export const useRenderCwRSpotlight = ({
	anchorSelector,
	choreographerMessageId,
	spotlightTarget,
	messages,
	placement,
	isEnabled,
	requireAnchorForStart = false,
	onCtaClick,
	onClosed,
	onBlocked,
}: UseRenderCwRSpotlightArgs): UseRenderCwRSpotlightResult => {
	const { formatMessage } = useIntl();
	const { createEvent } = useConfluenceAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();
	const { useChoreographer } = useChoreographerAssets();
	const {
		hasSeenSpotlight: hasSeenSpotlightInStorage,
		markSpotlightSeen: markSpotlightSeenInStorage,
	} = useCwRSpotlightSeenStorage();

	const isServerPlaceholder = useRenderServerPlaceholder();

	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const skipOnboardingStateQuery = hasSeenSpotlightInStorage || !isEnabled || isServerPlaceholder;
	const {
		data: onboardingStateData,
		loading: isOnboardingStateLoading,
		error: isOnboardingStateError,
	} = useGetOnboardingState(ONBOARDING_STATE_KEYS_TO_FETCH, skipOnboardingStateQuery);

	const { hasSeenUniversalCreateCwRExperimentSpotlight } = deserializeState(onboardingStateData);
	const shouldSuppressSpotlight =
		Boolean(hasSeenUniversalCreateCwRExperimentSpotlight) || hasSeenSpotlightInStorage;

	// When the backend onboarding state reports the spotlight has been seen but local storage has
	// not been updated yet, mirror it into local storage so subsequent renders short-circuit the
	// `useGetOnboardingState` query (which is otherwise re-run on every render).
	useEffect(() => {
		if (hasSeenUniversalCreateCwRExperimentSpotlight && !hasSeenSpotlightInStorage) {
			markSpotlightSeenInStorage();
		}
	}, [
		hasSeenUniversalCreateCwRExperimentSpotlight,
		hasSeenSpotlightInStorage,
		markSpotlightSeenInStorage,
	]);

	const anchorEl = useElementBySelector(anchorSelector, {
		disabled: isServerPlaceholder || shouldSuppressSpotlight || Boolean(isOnboardingStateError),
	});

	const hasStoppedRef = useRef<boolean>(false);

	const handleSpotlightStart = useCallback(() => {
		setIsSpotlightActive(true);
		hasStoppedRef.current = false;

		createEvent({
			type: 'sendScreenEvent',
			data: {
				name: SPOTLIGHT_ANALYTICS_ID,
				attributes: { source: SPOTLIGHT_ANALYTICS_ID, spotlightTarget },
			},
		}).fire();
	}, [createEvent, spotlightTarget]);

	const markSpotlightSeen = useCallback(() => {
		markSpotlightSeenInStorage();
		setOnboardingState({
			key: UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS.hasSeenUniversalCreateCwRExperimentSpotlight,
			value: 'true',
		});
	}, [markSpotlightSeenInStorage, setOnboardingState]);

	const handleSpotlightStop = useCallback(() => {
		setIsSpotlightActive(false);
	}, []);

	// eslint-disable-next-line -- useChoreographer is the stable hook exposed by choreographer context.
	const choreographer = useChoreographer(choreographerMessageId, {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const fireSpotlightDisplayRequestedEvent = useCallback(() => {
		createEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'requested',
				actionSubject: 'spotlight',
				actionSubjectId: SPOTLIGHT_ANALYTICS_ID,
				source: SPOTLIGHT_ANALYTICS_ID,
				attributes: { spotlightTarget },
			},
		}).fire();
	}, [createEvent, spotlightTarget]);

	const fireSpotlightNotDisplayedEvent = useCallback(
		(reason: SpotlightNotDisplayedReason) => {
			createEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'notDisplayed',
					actionSubject: 'spotlight',
					actionSubjectId: SPOTLIGHT_ANALYTICS_ID,
					source: SPOTLIGHT_ANALYTICS_ID,
					attributes: {
						choreographerMessageId,
						reason,
						spotlightTarget,
					},
				},
			}).fire();
		},
		[choreographerMessageId, createEvent, spotlightTarget],
	);

	const startSpotlight = useCallback(() => {
		if (
			isOnboardingStateLoading ||
			isOnboardingStateError ||
			shouldSuppressSpotlight ||
			(requireAnchorForStart && anchorEl === null)
		) {
			return false;
		}

		fireSpotlightDisplayRequestedEvent();

		void choreographer
			.startMessage()
			.then((status) => {
				if (status === CHOREOGRAPHER_BLOCKED_STATUS) {
					fireSpotlightNotDisplayedEvent('choreographerBlocked');
					onBlocked?.(spotlightTarget);
				}
			})
			.catch(() => {
				fireSpotlightNotDisplayedEvent('choreographerError');
			});

		return true;
	}, [
		choreographer,
		fireSpotlightNotDisplayedEvent,
		fireSpotlightDisplayRequestedEvent,
		onBlocked,
		spotlightTarget,
		shouldSuppressSpotlight,
		requireAnchorForStart,
		isOnboardingStateError,
		isOnboardingStateLoading,
		anchorEl,
	]);

	const stopSpotlight = useCallback(() => {
		if (hasStoppedRef.current) {
			return;
		}
		hasStoppedRef.current = true;
		void choreographer.stopMessage();
	}, [choreographer]);

	const closeSpotlight = useCallback(
		(action: 'clicked' | 'dismissed', interactionType: UniversalCreateSpotlightInteractionType) => {
			markSpotlightSeen();

			createEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject: 'spotlight',
					actionSubjectId: SPOTLIGHT_ANALYTICS_ID,
					source: SPOTLIGHT_ANALYTICS_ID,
					attributes: { interactionType, spotlightTarget },
				},
			}).fire();

			stopSpotlight();
			onClosed?.();
		},
		[createEvent, markSpotlightSeen, spotlightTarget, stopSpotlight, onClosed],
	);

	const handleDismiss = useCallback(
		(event: DismissEvent) => closeSpotlight('dismissed', getDismissInteractionType(event)),
		[closeSpotlight],
	);

	const handleCtaClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			closeSpotlight('clicked', 'cta');
			onCtaClick?.(event);
		},
		[closeSpotlight, onCtaClick],
	);

	const canRender =
		!isServerPlaceholder &&
		isEnabled &&
		anchorEl !== null &&
		!isOnboardingStateLoading &&
		!shouldSuppressSpotlight;

	const spotlight = canRender ? (
		<PopoverProvider>
			{createPortal(
				<Box
					aria-hidden
					testId="hidden-mounting-target"
					xcss={
						placement === 'left-end'
							? spotlightAnchorStyles.menuAnchorWrapper
							: spotlightAnchorStyles.buttonAnchorWrapper
					}
				>
					<PopoverTarget>
						<Box as="span" />
					</PopoverTarget>
				</Box>,
				anchorEl,
			)}
			<Portal zIndex={600}>
				<PopoverContent dismiss={handleDismiss} isVisible={isSpotlightActive} placement={placement}>
					<SpotlightUpdateOnChange selectors={[anchorSelector]} />
					<SpotlightCard testId="universal-create-cwr-experiment-spotlight">
						<SpotlightHeader>
							<SpotlightHeadline>{formatMessage(messages.title)}</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightMedia>
							<Image src={SplitButtonGif} alt="" width={295} height={135} />
						</SpotlightMedia>
						<SpotlightBody>
							<Stack space="space.150">
								<Text>
									<FormattedMessage {...messages.bodyLine1} values={RICH_TEXT_VALUES} />
								</Text>
								<Text>
									<FormattedMessage {...messages.bodyLine2} values={RICH_TEXT_VALUES} />
								</Text>
							</Stack>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={handleCtaClick}>
									{formatMessage(messages.cta)}
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</Portal>
		</PopoverProvider>
	) : null;

	return { spotlight, startSpotlight, stopSpotlight, isSpotlightActive };
};
